import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, BadgeCheck, ArrowLeft, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function CommunityPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/community/posts/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => toast.error("Post not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const toggleLike = async () => {
    if (!user) {
      toast.error("Sign in to react");
      navigate("/login");
      return;
    }
    setPost((p) =>
      p
        ? {
            ...p,
            liked_by_viewer: !p.liked_by_viewer,
            like_count: (p.like_count || 0) + (p.liked_by_viewer ? -1 : 1),
          }
        : p
    );
    try {
      await api.post(`/community/posts/${id}/like`);
    } catch {
      toast.error("Couldn't react");
      load();
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to comment"); navigate("/login"); return; }
    const text = comment.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/community/posts/${id}/comments`, { text });
      setComment("");
      load();
    } catch (err) {
      toast.error("Failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/community/comments/${cid}`);
      load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  if (loading) {
    return (
      <StoreLayout>
        <div className="p-20 text-center label-mono">// LOADING POST...</div>
      </StoreLayout>
    );
  }

  if (!post) {
    return (
      <StoreLayout>
        <div className="p-20 text-center" data-testid="post-not-found">
          <p className="label-mono text-[#71717A]">// POST NOT FOUND OR REMOVED</p>
          <Link to="/community" className="btn-neon mt-6 inline-flex">BACK TO THE WALL</Link>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link
          to="/community"
          className="inline-flex items-center gap-2 label-mono text-[#71717A] hover:text-[#CFF048] mb-6"
          data-testid="back-to-wall"
        >
          <ArrowLeft size={14} /> BACK TO THE WALL
        </Link>

        <div className="grid md:grid-cols-[1.2fr_1fr] gap-0 border border-[#27272A] bg-[#0A0A0A]">
          {/* Image */}
          <div className="bg-black flex items-center justify-center">
            <img
              src={absoluteImg(post.image_url)}
              alt={post.caption}
              className="max-h-[700px] w-full object-contain"
              data-testid="post-image"
            />
          </div>

          {/* Side panel */}
          <div className="flex flex-col">
            {/* Author */}
            <div className="px-6 py-5 border-b border-[#27272A] flex items-center gap-3">
              <div className="w-10 h-10 bg-[#CFF048] text-black font-display font-black flex items-center justify-center">
                {(post.user_name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold flex items-center gap-1.5">
                  {post.user_name || "Anonymous"}
                  <BadgeCheck size={14} className="text-[#CFF048]" />
                </p>
                <p className="label-mono text-[#71717A]">
                  // {new Date(post.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Caption + product */}
            <div className="px-6 py-5 border-b border-[#27272A]">
              {post.caption && (
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{post.caption}</p>
              )}
              {post.product_slug && (
                <Link
                  to={`/product/${post.product_slug}`}
                  className="inline-block mt-4 label-mono text-[#CFF048] hover:underline"
                  data-testid="post-product-link"
                >
                  // WEARING: {post.product_name} →
                </Link>
              )}
            </div>

            {/* Reactions */}
            <div className="px-6 py-4 border-b border-[#27272A] flex items-center gap-6">
              <button
                onClick={toggleLike}
                className={`flex items-center gap-2 label-mono transition-colors ${
                  post.liked_by_viewer ? "text-[#CFF048]" : "text-[#71717A] hover:text-white"
                }`}
                data-testid="post-detail-like-btn"
              >
                <Heart size={18} className={post.liked_by_viewer ? "fill-[#CFF048]" : ""} />
                {post.like_count || 0} LIKES
              </button>
              <span className="flex items-center gap-2 label-mono text-[#71717A]">
                <MessageCircle size={18} /> {post.comment_count || 0} COMMENTS
              </span>
            </div>

            {/* Comments */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 max-h-[400px]" data-testid="comments-list">
              {post.comments?.length === 0 && (
                <p className="label-mono text-[#71717A] text-center py-8">// NO COMMENTS YET. SPEAK UP.</p>
              )}
              {post.comments?.map((c) => {
                const mine = user && (c.user_id === user.id || user.role === "admin");
                return (
                  <div key={c.id} className="group" data-testid={`comment-${c.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 bg-[#27272A] text-white text-xs font-display font-black flex items-center justify-center flex-shrink-0">
                        {(c.user_name || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="font-display font-bold text-sm truncate">{c.user_name || "Anonymous"}</span>
                          <span className="label-mono text-[#52525B]">// {new Date(c.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-[#A1A1AA] text-sm mt-0.5 whitespace-pre-wrap break-words">{c.text}</p>
                      </div>
                      {mine && (
                        <button
                          onClick={() => deleteComment(c.id)}
                          className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-red-400 transition-opacity"
                          data-testid={`delete-comment-${c.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add comment */}
            <form onSubmit={addComment} className="px-4 py-3 border-t border-[#27272A] flex items-center gap-2 bg-black" data-testid="add-comment-form">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 300))}
                placeholder={user ? "Drop a reaction..." : "Sign in to comment"}
                disabled={!user}
                className="cult-input flex-1"
                data-testid="comment-input"
              />
              <button
                type="submit"
                disabled={busy || !comment.trim() || !user}
                className="btn-neon px-4 py-2 disabled:opacity-50"
                data-testid="send-comment-btn"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </StoreLayout>
  );
}
